import React, { useEffect, useState } from 'react';
import { useStore, uid } from '../state/store';
import { api } from '../state/api';
import {
  Card, Chip, Banner, Empty, SectionHead, Stat, EvidenceBadge, TermHelp,
} from '../components/ui';
import {
  AccountModal, OpportunityModal, AssetModal, TierChangeModal,
  blankAccount, blankOpportunity, blankAsset,
} from '../components/PipelineForms';
import { EMPTY_COPY } from '../lib/constants';
import { ACCOUNT_TIERS, candidateScore, statusRank } from '../lib/funnel';
import { fmtDate } from '../lib/dates';
import { parseCSV, rowsToRecords, ACCOUNT_ALIASES, OPPORTUNITY_ALIASES, findDuplicateAccounts } from '../lib/io';
import { namedCommercialMoves, readyMovesFromPackages } from '../lib/workPackages';
import { newAction } from '../lib/actions';
import { d001Surfacing } from '../lib/operatingMode';
import { orgPrimaryPipeline } from '../lib/orgPlan';

export default function CommercialPipeline({ go, embedded = false, pipeline: pipelineProp = null }) {
  const { state, d, add, patch, remove, today, reload } = useStore();
  const pipeline = pipelineProp || orgPrimaryPipeline(state) || {
    key: 'dubai',
    name: 'Pipeline',
    kind: 'asset_origination',
    defaultMarket: 'Dubai',
    defaultOpportunityType: 'Dubai Founding Asset',
    deepWorkspace: true,
  };
  const pipelineKey = pipeline.key || 'dubai';
  const f = d.funnel;
  const surfacing = d001Surfacing(d);
  const [accountDraft, setAccountDraft] = useState(null);
  const [oppDraft, setOppDraft] = useState(null);
  const [assetDraft, setAssetDraft] = useState(null);
  const [tierChange, setTierChange] = useState(null);
  const [importMsg, setImportMsg] = useState('');
  const [tierFilter, setTierFilter] = useState('All');
  const [crm, setCrm] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(true);

  const inPipeline = (row) => {
    const key = row?.pipelineKey;
    if (key) return key === pipelineKey;
    if (pipeline.defaultMarket && row?.market === pipeline.defaultMarket) return true;
    // Legacy Blocks rows without pipelineKey stay on the dubai pipeline.
    if (pipelineKey === 'dubai' && (!row?.market || row.market === 'Dubai')) return true;
    return false;
  };

  const accounts = (state.accounts || []).filter(inPipeline);
  const opportunities = (state.opportunities || []).filter(inPipeline);
  const qualify = f.qualify || { qualifiedTargets: [], tierA: [], all: [] };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await api.getCrmStatus();
        if (cancelled) return;
        setCrm(status);
        const shouldSync = status.connected && (!status.lastSyncAt || status.freshness === 'stale' || status.freshness === 'error');
        if (!shouldSync) return;
        setSyncing(true);
        const result = await api.syncCrm({ pipelineKey });
        if (cancelled) return;
        setCrm(result.status);
        await reload();
      } catch (err) {
        if (!cancelled) {
          setCrm((prev) => ({ ...(prev || {}), lastError: err.message, freshness: 'error' }));
        }
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload, pipelineKey]);

  const syncNow = async () => {
    setSyncing(true);
    setImportMsg('');
    try {
      const result = await api.syncCrm({ pipelineKey });
      setCrm(result.status);
      await reload();
      setImportMsg(
        `Synced ${result.status?.accountsSynced || 0} Nexus account(s)` +
          `${result.opportunitiesCreated != null ? `, ${result.opportunitiesCreated + (result.opportunitiesUpdated || 0)} opportunity shell(s)` : ''}` +
          `. ${result.created} new accounts, ${result.updated} updated.` +
          `${result.opportunitiesUnlinked ? ` ${result.opportunitiesUnlinked} opportunity shell(s) had no matching Nexus account.` : ''}` +
          ` Shells stay unqualified until Bridge criteria are met; CRM next steps carry over when present.`,
      );
    } catch (err) {
      setCrm((prev) => ({ ...(prev || {}), lastError: err.message, freshness: 'error' }));
      setImportMsg(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const importAccounts = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const recs = rowsToRecords(parseCSV(String(reader.result)), ACCOUNT_ALIASES, {
        market: pipeline.defaultMarket || '', tier: 'Universe', status: 'Universe', evidenceLevel: 'E0',
        pipelineKey,
      });
      const { fresh, dupes } = findDuplicateAccounts(accounts, recs);
      const created = fresh.map((r) => ({
        ...blankAccount(today, { pipelineKey, market: pipeline.defaultMarket || '' }),
        ...r,
        id: uid('acc'),
        pipelineKey,
        isNew: false,
        history: [{ stage: r.status || 'Universe', date: today }],
        stageSince: today,
      }));
      created.forEach((row) => add('accounts', row));
      const allAccounts = [...accounts, ...created];
      const named = namedCommercialMoves(allAccounts, state.top3 || [], today);
      const namedActions = named.map((m) => newAction(m, today));
      namedActions.forEach((row) => add('top3', row));
      const extras = readyMovesFromPackages(
        (state.workPackages || []).filter((p) => !p.setup),
        [...(state.top3 || []), ...namedActions],
        today,
      );
      extras.forEach((m) => add('top3', newAction({ ...m, selectedForToday: false, status: 'Ready' }, today)));
      setImportMsg(
        `${created.length} account(s) imported.${dupes.length ? ` ${dupes.length} skipped as duplicates: ${dupes.slice(0, 5).map((x) => x.name).join(', ')}${dupes.length > 5 ? '…' : ''}` : ''} Commercial outreach regenerated — Accept named moves on Command to fill Today.`
      );
    };
    reader.readAsText(file);
  };

  const importOpportunities = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const recs = rowsToRecords(parseCSV(String(reader.result)), OPPORTUNITY_ALIASES, {
        market: pipeline.defaultMarket || '',
        stage: 'Qualified',
        evidenceLevel: 'E2',
        type: pipeline.defaultOpportunityType || '',
        pipelineKey,
      });
      let created = 0;
      let newAccounts = 0;
      const norm = (s) => String(s || '').trim().toLowerCase();
      const index = new Map(accounts.map((a) => [norm(a.name), a]));
      recs.forEach((r) => {
        if (!String(r.name || '').trim()) return;
        let acc = index.get(norm(r.accountName));
        if (!acc && String(r.accountName || '').trim()) {
          acc = {
            ...blankAccount(today, { pipelineKey, market: pipeline.defaultMarket || '' }),
            id: uid('acc'),
            name: r.accountName,
            tier: 'Qualified Target',
            status: 'Discovery',
            pipelineKey,
            isNew: false,
          };
          index.set(norm(r.accountName), acc);
          add('accounts', acc);
          newAccounts += 1;
        }
        add('opportunities', {
          ...blankOpportunity(today, acc, {
            pipelineKey,
            market: pipeline.defaultMarket || '',
            type: pipeline.defaultOpportunityType || '',
          }),
          ...r,
          id: uid('opp'),
          accountId: acc?.id || '',
          pipelineKey,
          isNew: false,
          history: [{ stage: r.stage || 'Qualified', date: today }],
        });
        created += 1;
      });
      setImportMsg(`${created} opportunity(ies) imported.${newAccounts ? ` ${newAccounts} new account(s) created from the Account column.` : ''}`);
    };
    reader.readAsText(file);
  };

  const saveAccount = (a) => {
    if (accounts.some((x) => x.id === a.id)) patch('accounts', a.id, a);
    else add('accounts', a);
  };

  const saveOpportunity = (o) => {
    if (opportunities.some((x) => x.id === o.id)) patch('opportunities', o.id, o);
    else add('opportunities', o);
  };

  const saveAsset = (a) => {
    if ((state.assetCandidates || []).some((x) => x.id === a.id)) patch('assetCandidates', a.id, a);
    else {
      add('assetCandidates', a);
      if (a.opportunityId) patch('opportunities', a.opportunityId, { assetIdentified: true });
    }
  };

  const acceptTier = ({ tier, reason, note }) => {
    const account = tierChange.account;
    patch('accounts', account.id, {
      tier,
      tierReason: `${reason}${note ? ` — ${note}` : ''}`,
      tierChangedOn: today,
    });
    add('decisions', {
      id: uid('dec'),
      date: today,
      type: 'Market',
      question: `Tier change for ${account.name}`,
      options: 'Tier A / Tier B / Parked',
      evidence: note || reason,
      decision: `Moved to ${tier} — ${reason}`,
      reviewDate: '',
    });
    if (tier === 'Tier A') {
      const named = namedCommercialMoves([{ ...account, tier }], state.top3 || [], today, { onlyTierA: true });
      named.forEach((m) => add('top3', newAction(m, today)));
    }
  };

  const visibleAccounts = accounts.filter((a) => tierFilter === 'All' || a.tier === tierFilter);
  const freshnessLabel = crmFreshness(crm);
  const importTone = /failed|error/i.test(importMsg || '') ? 'bad' : 'ok';

  return (
    <>
      {!embedded && (
        <div className="page-head">
          <div className="row">
            <h2>DUBAI PIPELINE</h2>
            <Chip tone={healthTone(f.health.status)}>{f.health.status}</Chip>
            <Chip tone={concTone(f.concentration.level)}>Concentration {f.concentration.level}</Chip>
          </div>
          <div className="sub">The commercial machine that must produce D001.</div>
        </div>
      )}

      <section className="section">
        <Card>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <span>
              Source: Nexus CRM · {crm?.accountsSynced ?? accounts.length} accounts · Last synced {crm?.lastSyncAt ? fmtDate(crm.lastSyncAt) : 'never'} · {freshnessLabel}
            </span>
            <div className="spacer" />
            <button className="btn sm primary" disabled={syncing || !crm?.connected} onClick={syncNow}>
              {syncing ? 'Syncing…' : 'Sync from Nexus'}
            </button>
            {crm?.webBaseUrl && (
              <a className="btn sm" href={crm.webBaseUrl} target="_blank" rel="noreferrer">View all in CRM</a>
            )}
          </div>
          {crm?.lastError && <div className="tiny" style={{ marginTop: 8, color: 'var(--red)' }}>{crm.lastError}</div>}
          {!crm?.connected && (
            <div className="dim tiny" style={{ marginTop: 8 }}>
              Connect org ID + API key in Settings. CSV import remains a fallback.
            </div>
          )}
        </Card>
      </section>

      {importMsg && <Banner tone={importTone}>{importMsg}</Banner>}

      <section className="section">
        <SectionHead
          title="Funnel"
          hint="Operating hypothesis — adjust from evidence after Day 15."
          term="marketUniverse"
        />
        <Card>
          <div className="funnel">
            {f.steps.map((s, i) => {
              const pct = s.target ? Math.min(100, Math.round((s.count / s.target) * 100)) : null;
              const stepTerm =
                s.key === 'universe' ? 'marketUniverse'
                  : s.key === 'qualifiedTargets' ? 'qualifiedTargets'
                    : s.key === 'discovery' ? 'substantiveConversations'
                      : s.key === 'qualified' ? 'qualifiedOpportunities'
                        : s.key === 'assetsIdentified' ? 'credibleAssets'
                          : s.key === 'candidates' ? 'd001Candidates'
                            : s.key === 'primary' ? 'primaryD001'
                              : undefined;
              return (
                <div className="funnel-step" key={s.key}>
                  <div className="fs-count num">{s.count}</div>
                  <div className="fs-label">
                    <span>{s.label}</span>
                    {stepTerm ? <TermHelp term={stepTerm} label={s.label} /> : null}
                  </div>
                  {s.target ? (
                    <>
                      <div className="meter" style={{ marginTop: 6 }}>
                        <span className={pct >= 100 ? 'green' : pct >= 50 ? 'amber' : 'flat'} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="fs-target dim tiny">target {s.target}</div>
                    </>
                  ) : (
                    <div className="fs-target dim tiny">&nbsp;</div>
                  )}
                  {i < f.steps.length - 1 && <span className="fs-arrow">→</span>}
                </div>
              );
            })}
          </div>
          <div className="hr" />
          <div className="row">
            <span className="dim tiny">
              Primary D001: <strong>{f.counts.primary} / 1</strong>
              {f.primary ? ` · ${f.primary.name}` : ' · not selected yet'}
              {f.counts.backups ? ` · ${f.counts.backups} backup(s)` : ' · no backup'}
            </span>
            <div className="spacer" />
            {surfacing.level === 'primary' && (
              <button className="btn sm" onClick={() => go('primary-deal')}>Open D001</button>
            )}
            {surfacing.level === 'candidate' && (
              <span className="dim tiny">Compare candidates below — designate Primary when ready</span>
            )}
          </div>
        </Card>
      </section>

      {accounts.length > 0 && (
        <section className="section">
          <SectionHead
            title="Founder queue"
            hint={`Maximum 15. You do not work the ${f.counts.universe}-account universe.`}
            right={<span className="dim tiny">{f.queue.length} item(s)</span>}
          />
          {f.queue.length === 0 ? (
            <Empty title={EMPTY_COPY.queue}>Confirm priority accounts in Finish Setup, or set a next action on a priority account.</Empty>
          ) : (
            <Card flush>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Why now</th>
                      <th style={{ width: 130 }}>Stage</th>
                      <th style={{ width: 60 }}>E-level</th>
                      <th style={{ width: 90 }}>Last touch</th>
                      <th>Next move</th>
                      <th style={{ width: 90 }}>Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.queue.map((q) => (
                      <tr key={q.key} style={q.overdue ? { background: 'var(--red-bg)' } : undefined}>
                        <td>{q.account}</td>
                        <td className="tiny">{q.why}</td>
                        <td>{q.stage}</td>
                        <td><EvidenceBadge level={q.evidenceLevel} /></td>
                        <td className="num">{q.lastTouch ? fmtDate(q.lastTouch) : '—'}</td>
                        <td>{q.nextAction || <span className="dim">none set</span>}</td>
                        <td className="num">{q.nextActionDue ? fmtDate(q.nextActionDue) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </section>
      )}

      {(qualify.all || []).length > 0 && (
        <section className="section">
          <SectionHead title="Founder approval" hint="Bridge45 proposes Qualified Target and Tier A. Counts do not move until you accept." />
          <Card>
            {qualify.tierA.map((p) => (
              <div className="row" key={`ta-${p.id}`} style={{ padding: '5px 0', borderBottom: '1px solid var(--line-2)' }}>
                <Chip tone="accent">Tier A</Chip>
                <span style={{ flex: 1 }}>{p.name}</span>
                <span className="dim tiny">{p.reason}</span>
                <button className="btn sm" onClick={() => setTierChange({ account: accounts.find((a) => a.id === p.id), direction: 'promote' })}>Review</button>
              </div>
            ))}
            {qualify.qualifiedTargets.map((p) => (
              <div className="row" key={`qt-${p.id}`} style={{ padding: '5px 0', borderBottom: '1px solid var(--line-2)' }}>
                <Chip>Qualified Target</Chip>
                <span style={{ flex: 1 }}>{p.name}</span>
                <span className="dim tiny">{p.reason}</span>
                <button className="btn sm" onClick={() => setTierChange({ account: accounts.find((a) => a.id === p.id), direction: 'promote' })}>Review</button>
              </div>
            ))}
          </Card>
        </section>
      )}

      <section className="section grid g4">
        <Card title="Funnel health" right={<Chip tone={healthTone(f.health.status)}>{f.health.status}</Chip>}>
          <div className="tiny muted">{f.health.why}</div>
        </Card>
        <Card title="Deal concentration" right={<Chip tone={concTone(f.concentration.level)}>{f.concentration.level}</Chip>}>
          <div className="tiny muted">{f.concentration.why}</div>
        </Card>
        <Card title="Funnel velocity">
          {f.velocity.enough ? (
            <>
              {f.velocity.rows.slice(0, 4).map((r) => (
                <div className="row tight" key={r.label} style={{ justifyContent: 'space-between' }}>
                  <span className="tiny">{r.label}</span>
                  <span className="num tiny">{r.median}d <span className="dim">n={r.n}</span></span>
                </div>
              ))}
              <div className="dim tiny" style={{ marginTop: 6 }}>Median elapsed days, from your own history.</div>
            </>
          ) : (
            <div className="dim tiny">Not enough data yet. Velocity appears once stages have moved a few times.</div>
          )}
        </Card>
        <Card title="45-day projection">
          {f.projection.available ? (
            <>
              <div className="mid-num">{f.projection.value}</div>
              <div className="tiny muted" style={{ marginTop: 6 }}>{f.projection.text}</div>
              <div className="dim tiny" style={{ marginTop: 6 }}>{f.projection.caveat}</div>
            </>
          ) : (
            <div className="dim tiny">Not enough data yet.</div>
          )}
        </Card>
      </section>

      <section className="section">
        <SectionHead title="Where is it leaking?" hint="Conversion is computed from your data only. No industry benchmarks." />
        <div className="grid g-1-2">
          <Card title="Stage conversion" flush>
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {f.conversions.map((c) => (
                    <tr key={c.label}>
                      <td>{c.label}</td>
                      <td className="num" style={{ width: 90 }}>{c.from} → {c.to}</td>
                      <td className="num" style={{ width: 70 }}>{c.rate === null ? '—' : `${c.rate}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card title="Diagnosis">
            {f.diagnosis.length === 0 ? (
              <div className="dim tiny">
                No structural leak detected in the recorded data. That is not the same as a healthy funnel — check the
                counts against the operating hypothesis above.
              </div>
            ) : (
              f.diagnosis.map((x) => (
                <div className="alert medium" key={x.key}>
                  <span style={{ flex: 1 }}>
                    <span className="t">
                      {x.stage}
                      {x.stage === 'Asset commitment' ? (
                        <TermHelp term="assetCommitment" label="Asset commitment" />
                      ) : null}
                    </span>
                    <span className="d">{x.text}</span>
                  </span>
                </div>
              ))
            )}
          </Card>
        </div>
      </section>

      <section className="section">
        <SectionHead title="AI-assisted pipeline" hint="Tier B and below. Research, enrichment, contact mapping, follow-up preparation." />
        <div className="grid g-1-2">
          <Card title="Volume">
            <div className="grid g2">
              <Stat label="Tier B" value={<span className="num">{f.counts.tierB}</span>} />
              <Stat label="Universe (unqualified)" term="marketUniverse" value={<span className="num">{Math.max(0, f.counts.universe - f.counts.qualifiedTargets)}</span>} />
              <Stat label="Parked / rejected" value={<span className="num">{f.counts.parked}</span>} size="sm" />
              <Stat label="Qualified targets" term="qualifiedTargets" value={<span className="num">{f.counts.qualifiedTargets}</span>} size="sm" />
            </div>
          </Card>
          <Card title="Promotion recommendations" sub="Suggestions. A tier change still requires a reason.">
            {f.promotable.promote.length === 0 && f.promotable.demote.length === 0 ? (
              <div className="dim tiny">Nothing to promote or demote on current data.</div>
            ) : (
              <>
                {f.promotable.promote.map((p) => (
                  <div className="row" key={p.id} style={{ padding: '5px 0', borderBottom: '1px solid var(--line-2)' }}>
                    <Chip tone="green">Promote</Chip>
                    <span style={{ flex: 1 }}>{p.name}</span>
                    <span className="dim tiny">{p.reason}</span>
                    <button className="btn sm" onClick={() => setTierChange({ account: accounts.find((a) => a.id === p.id), direction: 'promote' })}>Change tier</button>
                  </div>
                ))}
                {f.promotable.demote.map((p) => (
                  <div className="row" key={p.id} style={{ padding: '5px 0', borderBottom: '1px solid var(--line-2)' }}>
                    <Chip tone="amber">Demote</Chip>
                    <span style={{ flex: 1 }}>{p.name}</span>
                    <span className="dim tiny">{p.reason}</span>
                    <button className="btn sm" onClick={() => setTierChange({ account: accounts.find((a) => a.id === p.id), direction: 'demote' })}>Change tier</button>
                  </div>
                ))}
              </>
            )}
          </Card>
        </div>
      </section>

      <section className="section">
        <SectionHead
          title="Accounts"
          hint="Founder-thin shells. Nexus remains the system of record."
          right={
            <div className="row tight">
              <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} style={{ width: 160 }}>
                <option value="All">All tiers</option>
                {ACCOUNT_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button className="btn sm" onClick={() => setAccountsOpen((v) => !v)}>{accountsOpen ? 'Collapse' : 'Show list'}</button>
              <button className="btn sm primary" onClick={() => setAccountDraft(blankAccount(today, { pipelineKey, market: pipeline.defaultMarket || '' }))}>Add account</button>
            </div>
          }
        />
        {accounts.length === 0 ? (
          <Empty title={EMPTY_COPY.noAccounts}>
            {crm?.connected
              ? 'Use Sync from Nexus. CSV import is a fallback if the CRM is unavailable.'
              : 'Connect Nexus in Settings, or add the first names by hand.'}
            <div className="row tight" style={{ marginTop: 10 }}>
              <label className="btn sm">
                Import accounts CSV
                <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => e.target.files[0] && importAccounts(e.target.files[0])} />
              </label>
            </div>
          </Empty>
        ) : accountsOpen ? (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th style={{ width: 130 }}>Tier</th>
                    <th style={{ width: 130 }}>Status</th>
                    <th style={{ width: 90 }}>Last touch</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {visibleAccounts.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div>{a.name || <span className="dim">Unnamed</span>}</div>
                        <div className="dim tiny">{a.segment}{a.country ? ` · ${a.country}` : ''}{a.source === 'nexus' || a.nexusId ? ' · Nexus' : ''}</div>
                      </td>
                      <td><Chip tone={a.tier === 'Tier A' ? 'accent' : ''}>{a.tier}</Chip></td>
                      <td>{a.status}</td>
                      <td className="num">{a.lastTouch ? fmtDate(a.lastTouch) : '—'}</td>
                      <td className="actions nowrap">
                        <button className="btn sm ghost" onClick={() => setTierChange({ account: a, direction: statusRank(a.status) >= 3 ? 'promote' : 'demote' })}>Tier</button>
                        <button className="btn sm ghost" onClick={() => setOppDraft(blankOpportunity(today, a, { pipelineKey, market: pipeline.defaultMarket || '', type: pipeline.defaultOpportunityType || '' }))}>+ Opp</button>
                        <button className="btn sm ghost" onClick={() => setAccountDraft(a)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row tight" style={{ padding: 10 }}>
              <label className="btn sm">
                Import accounts CSV
                <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => e.target.files[0] && importAccounts(e.target.files[0])} />
              </label>
              <label className="btn sm">
                Import opportunities CSV
                <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => e.target.files[0] && importOpportunities(e.target.files[0])} />
              </label>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="dim tiny">{visibleAccounts.length} account(s) in this filter. Open the list to inspect shells — do not treat this as a second CRM.</div>
          </Card>
        )}
      </section>

      <section className="section">
        <SectionHead
          title="Opportunities"
          hint="Created only when a specific commercial problem or use case exists."
          right={<button className="btn sm primary" disabled={accounts.length === 0} onClick={() => setOppDraft(blankOpportunity(today, accounts[0], { pipelineKey, market: pipeline.defaultMarket || '', type: pipeline.defaultOpportunityType || '' }))}>Add opportunity</button>}
        />
        {opportunities.length === 0 ? (
          <Empty title={EMPTY_COPY.noCandidates}>
            Convert discovery conversations into opportunities only when a real problem and a next commercial step exist.
          </Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Opportunity</th>
                    <th style={{ width: 150 }}>Account</th>
                    <th style={{ width: 150 }}>Stage</th>
                    <th style={{ width: 60 }}>E</th>
                    <th style={{ width: 80 }}>Score</th>
                    <th>Next action</th>
                    <th style={{ width: 90 }}>Due</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((o) => {
                    const sc = candidateScore(o);
                    return (
                      <tr key={o.id}>
                        <td>
                          <div className="row tight">
                            <span>{o.name || <span className="dim">Unnamed</span>}</span>
                            {o.primaryD001 && <Chip tone="accent">Primary</Chip>}
                            {!o.primaryD001 && o.d001Candidate && <Chip>Candidate</Chip>}
                          </div>
                          <div className="dim tiny">{o.type}</div>
                        </td>
                        <td>{o.accountName || '—'}</td>
                        <td>{o.stage}</td>
                        <td><EvidenceBadge level={o.evidenceLevel || 'E0'} /></td>
                        <td className="num">{sc.scored ? `${sc.total}/50` : '—'}</td>
                        <td className="tiny">{o.nextAction || <span className="dim">none</span>}</td>
                        <td className="num">{o.nextActionDue ? fmtDate(o.nextActionDue) : '—'}</td>
                        <td className="actions nowrap">
                          <button className="btn sm ghost" onClick={() => setAssetDraft(blankAsset(o, accounts.find((x) => x.id === o.accountId)))}>+ Asset</button>
                          <button className="btn sm ghost" onClick={() => setOppDraft(o)}>Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <section className="section">
        <SectionHead title="Asset candidates" right={<button className="btn sm" disabled={opportunities.length === 0} onClick={() => setAssetDraft(blankAsset(opportunities[0], null))}>Add asset</button>} />
        {(state.assetCandidates || []).length === 0 ? (
          <Empty title="No asset has surfaced yet.">
            An opportunity without an asset is a conversation. Asset information is the first thing a sponsor gives up
            that costs them something — it is E3.
          </Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th style={{ width: 150 }}>Opportunity</th>
                    <th style={{ width: 130 }}>Location</th>
                    <th style={{ width: 130 }}>Value</th>
                    <th style={{ width: 120 }}>Title status</th>
                    <th style={{ width: 120 }}>Screen</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {state.assetCandidates.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}<div className="dim tiny">{a.type}</div></td>
                      <td className="tiny">{opportunities.find((o) => o.id === a.opportunityId)?.name || <span className="dim">unlinked</span>}</td>
                      <td>{a.location}</td>
                      <td className="num">{a.indicativeValue || '—'}</td>
                      <td className="tiny">{a.ownershipStatus || <span className="dim">unknown</span>}</td>
                      <td>
                        <Chip tone={a.screenStatus === 'Passed' ? 'green' : a.screenStatus === 'Failed' ? 'red' : a.screenStatus === 'In screen' ? 'amber' : ''}>
                          {a.screenStatus}
                        </Chip>
                      </td>
                      <td className="actions">
                        <button className="btn sm ghost" onClick={() => setAssetDraft(a)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {accountDraft && (
        <AccountModal
          draft={accountDraft}
          duplicateNames={accounts.map((a) => a.name)}
          onClose={() => setAccountDraft(null)}
          onSave={saveAccount}
          onDelete={(id) => remove('accounts', id)}
        />
      )}
      {oppDraft && (
        <OpportunityModal
          draft={oppDraft}
          accounts={accounts}
          onClose={() => setOppDraft(null)}
          onSave={saveOpportunity}
          onDelete={(id) => remove('opportunities', id)}
        />
      )}
      {assetDraft && (
        <AssetModal
          draft={assetDraft}
          opportunities={opportunities}
          onClose={() => setAssetDraft(null)}
          onSave={saveAsset}
          onDelete={(id) => remove('assetCandidates', id)}
        />
      )}
      {tierChange && tierChange.account && (
        <TierChangeModal
          account={tierChange.account}
          direction={tierChange.direction}
          onClose={() => setTierChange(null)}
          onSave={acceptTier}
        />
      )}
    </>
  );
}

function crmFreshness(crm) {
  if (!crm || !crm.connected) return 'Disconnected';
  if (crm.freshness === 'stale' || !crm.lastSyncAt) return 'STALE';
  if (crm.freshness === 'error') return 'Error';
  return 'Sync healthy';
}

function healthTone(status) {
  return status === 'Strong' ? 'green' : status === 'Adequate' ? 'green' : status === 'Thin' ? 'amber' : 'red';
}

function concTone(level) {
  return level === 'Low' ? 'green' : level === 'Medium' ? 'amber' : 'red';
}
